import type { IResult } from 'ua-parser-js';
import { UAParser } from 'ua-parser-js';

interface Component
{
    key: string;
    value: any;
}

interface FingerprintConfig
{
    screenResolution: boolean;
    timezone: boolean;
    language: boolean;
    platform: boolean;
    plugins: boolean;
    canvas: boolean;
    webgl: boolean;
    fonts: boolean;
    audio: boolean;
    userAgent: boolean;
}

interface FingerprintResult
{
    hash: string;
    components: Record<string, any>;
    device: IResult;
}

async function getScreenResolution()
{
    return <Component>{
        key: 'screenResolution',
        value: `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`
    };
}

function getTimeZone()
{
    return <Component>{
        key: 'timezone',
        value: new Date().getTimezoneOffset()
    };
}

function getLanguage()
{
    return <Component>{
        key: 'language',
        value: navigator.language || navigator.languages[0]
    };
}

function getPlatform(agent: IResult)
{
    return <Component>{
        key: 'platform',
        value: agent.device.type
    };
}

function getPlugins()
{
    const plugins: string[] = [];

    for (let i = 0; i < navigator.plugins.length; i++)
    {
        plugins.push(navigator.plugins[i].name);
    }

    return <Component>{
        key: 'plugins',
        value: plugins.join(',')
    };
}

function getUserAgent(agent: IResult)
{
    return <Component>{
        key: 'userAgent',
        value: `${agent.browser.name} on ${agent.os.name} ${agent.os.version}`
    };
}

function getCanvasFingerprint()
{
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx)
    {
        return <Component>{
            key: 'canvas',
            value: 'not_supported'
        };
    }

    // Rysowanie tekstu
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillStyle = '#f60';
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = '#069';
    ctx.fillText('Browser Fingerprint', 2, 15);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.fillText('Browser Fingerprint', 4, 17);

    return <Component>{
        key: 'canvas',
        value: canvas.toDataURL()
    };
}

function getWebGLFingerprint()
{
    const canvas = document.createElement('canvas');
    const gl = (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')) as WebGLRenderingContext;

    if (!gl)
    {
        return <Component>{
            key: 'webgl',
            value: 'not_supported'
        };
    }

    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');

    if (!debugInfo)
    {
        return <Component>{
            key: 'webgl',
            value: 'no_debug_info'
        };
    }

    return <Component>{
        key: 'webgl',
        value: `${gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL)}-${gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)}`
    };
}

function getFonts()
{
    const baseFonts = ['monospace', 'sans-serif', 'serif'];
    const fontList = [
        'Arial', 'Verdana', 'Helvetica', 'Times New Roman', 'Courier New',
        'Georgia', 'Palatino', 'Garamond', 'Bookman', 'Tahoma', 'Trebuchet MS'
    ];

    const d = document.createElement('div');

    d.style.visibility = 'hidden';
    d.style.position = 'absolute';
    d.innerHTML = 'abcdefghijklmnopqrstuvwxyz0123456789';
    document.body.appendChild(d);

    const defaultWidth: { [key: string]: number } = {};

    baseFonts.forEach((baseFont) =>
    {
        defaultWidth[baseFont] = d.offsetWidth;
    });

    const detectedFonts = fontList.filter((font) =>
    {
        let matched = 0;

        for (const baseFont of baseFonts)
        {
            d.style.fontFamily = `'${font}',${baseFont}`;

            if (d.offsetWidth !== defaultWidth[baseFont])
            {
                matched++;
            }
        }

        return matched >= baseFonts.length - 1;
    });

    document.body.removeChild(d);

    return <Component>{
        key: 'fonts',
        value: detectedFonts.join(',')
    };
}

async function getAudioFingerprint()
{
    try
    {
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const analyser = audioContext.createAnalyser();
        const gainNode = audioContext.createGain();
        const scriptProcessor = audioContext.createScriptProcessor(4096, 1, 1);

        gainNode.gain.value = -25;
        oscillator.type = 'triangle';
        oscillator.connect(analyser);
        analyser.connect(gainNode);
        gainNode.connect(scriptProcessor);
        scriptProcessor.connect(audioContext.destination);

        oscillator.start(0);

        const audioData = new Float32Array(analyser.frequencyBinCount);

        analyser.getFloatFrequencyData(audioData);

        return <Component>{
            key: 'audio',
            value: Array.from(audioData).join(',')
        };
    }
    catch (e)
    {
        return <Component>{
            key: 'audio',
            value: 'not_supported'
        };
    }
}

async function collectComponents(ua: IResult, config: FingerprintConfig)
{
    const components: Component[] = [];

    if (config.screenResolution === true)
    {
        components.push(await getScreenResolution());
    }

    if (config.timezone === true)
    {
        components.push(getTimeZone());
    }

    if (config.language === true)
    {
        components.push(getLanguage());
    }

    if (config.platform === true)
    {
        components.push(getPlatform(ua));
    }

    if (config.plugins === true)
    {
        components.push(getPlugins());
    }

    if (config.canvas === true)
    {
        components.push(getCanvasFingerprint());
    }

    if (config.webgl === true)
    {
        components.push(getWebGLFingerprint());
    }

    if (config.fonts === true)
    {
        components.push(getFonts());
    }

    if (config.audio === true)
    {
        components.push(await getAudioFingerprint());
    }

    if (config.userAgent === true)
    {
        components.push(getUserAgent(ua));
    }

    return components.record(p => p.key, p => p.value);
}

async function generateHash(str: string): Promise<string>
{
    try
    {
        const msgBuffer = new TextEncoder().encode(str);
        const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
        const hashArray = Array.from(new Uint8Array(hashBuffer));

        return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    }
    catch (e)
    {
        let hash = 0;

        for (let i = 0; i < str.length; i++)
        {
            const char = str.charCodeAt(i);

            hash = ((hash << 5) - hash) + char;
            hash = hash & hash;
        }

        return Math.abs(hash).toString(36);
    }
}

async function getFingerprint(config: Partial<FingerprintConfig> = {}): Promise<FingerprintResult>
{
    const device = new UAParser().getResult();
    const components = await collectComponents(device, {
        screenResolution: true,
        timezone: false,
        language: true,
        platform: true,
        plugins: true,
        canvas: false,
        webgl: false,
        fonts: true,
        audio: false,
        userAgent: true,
        ...config
    });

    return {
        hash: await generateHash(JSON.stringify(components)),
        components,
        device
    };
}

export {
    getFingerprint
};

export type {
    FingerprintConfig,
    FingerprintResult
};
